<template>
  <div class="product-detail">
    <!-- 顶部导航栏 -->
    <van-nav-bar
      title="商品详情"
      left-arrow
      fixed
      placeholder
      @click-left="onClickLeft"
      :border="false"
      class="nav-bar"
    />

    <!-- 主要内容区域 -->
    <div class="content">
      <!-- 商品图片 -->
      <div class="image-section" @click="previewImage">
        <van-image
          :src="product.imgUrl"
          fit="cover"
          width="100%"
          height="100%"
          radius="0"
          class="product-image"
        >
          <template #loading>
            <van-loading type="spinner" size="20" />
          </template>
          <template #error>
            <div class="error-slot">
              <van-icon name="photo-fail" size="36" />
            </div>
          </template>
        </van-image>
      </div>

      <!-- 商品信息卡片 -->
      <div class="info-card">
        <!-- 商品信息 -->
        <div class="info-section">
          <div class="product-name">{{ product.name }}</div>
        </div>

        <!-- 底部按钮 -->
        <div class="bottom-section">
          <div class="navigation-buttons">
            <van-button 
              v-if="adjacentProducts.prev"
              block 
              round 
              color="#c62f2f"
              class="nav-button"
              @click="navigateToProduct('prev')"
            >
              上一件
            </van-button>
            <van-button 
              v-if="adjacentProducts.next"
              block 
              round 
              color="#c62f2f"
              class="nav-button"
              @click="navigateToProduct('next')"
            >
              下一件
            </van-button>
          </div>
          <van-button 
            block 
            round 
            color="#c62f2f"
            class="back-button"
            @click="onClickLeft"
          >
            返回上一页
          </van-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { showImagePreview, showToast } from 'vant';
import request from '@/utils/request';

const router = useRouter();
const route = useRoute();
const product = ref({});
const adjacentProducts = ref({
  prev: null,
  next: null
});

// 返回上一页
const onClickLeft = () => {
  router.back();
};

// 预览图片
const previewImage = () => {
  if (product.value.imgUrl) {
    showImagePreview({
      images: [product.value.imgUrl],
      closeable: true,
      closeIconPosition: 'top-right'
    });
  }
};

// 获取相邻商品信息
const fetchAdjacentProducts = async () => {
  try {
    const response = await request.get(`/api/v1/cinnabarOpen/product/adjacent/${route.params.id}`);
    if (response.code === '00000') {
      adjacentProducts.value = response.data;
    }
  } catch (error) {
    console.error('获取相邻商品信息失败:', error);
  }
};

// 获取商品详情
const fetchProductDetail = async () => {
  try {
    const response = await request.post('/api/v1/cinnabarOpen/selectAllProduct', {
      id: route.params.id
    });
    if (response.code === '00000' && response.data?.records && response.data.records.length > 0) {
      product.value = response.data.records[0];
      // 获取相邻商品信息
      await fetchAdjacentProducts();
    } else {
      showToast('获取商品信息失败');
    }
  } catch (error) {
    console.error('获取商品详情失败:', error);
    showToast('获取商品信息失败，请重试');
  }
};

// 切换到相邻商品
const navigateToProduct = async (direction) => {
  const targetProduct = adjacentProducts.value[direction];
  if (targetProduct) {
    try {
      await router.replace(`/product-detail/${targetProduct.id}`);
      // 等待路由更新后再获取数据
      await fetchProductDetail();
    } catch (error) {
      console.error('切换商品失败:', error);
      showToast('切换商品失败，请重试');
    }
  } else {
    showToast(direction === 'prev' ? '已经是第一个商品' : '已经是最后一个商品');
  }
};

onMounted(() => {
  fetchProductDetail();
});
</script>

<style scoped>
.product-detail {
  min-height: 100vh;
  background-color: #f7f8fa;
}

.nav-bar {
  background-color: transparent;
  backdrop-filter: blur(10px);
}

:deep(.van-nav-bar) {
  background-color: rgba(255, 255, 255, 0.95);
}

:deep(.van-nav-bar__title) {
  color: #333;
  font-weight: 600;
}

:deep(.van-nav-bar .van-icon) {
  color: #333;
}

.content {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.image-section {
  width: 100%;
  height: 550px;
  position: relative;
  background-color: #fff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  margin: 12px;
  box-sizing: border-box;
  cursor: pointer;
}

.product-image {
  transition: transform 0.3s ease;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.product-image:active {
  transform: scale(1.02);
}

.error-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #f8f8f8;
  color: #969799;
}

.info-card {
  flex: 1;
  background: #fff;
  border-radius: 16px 16px 0 0;
  margin-top: -20px;
  position: relative;
  z-index: 1;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.05);
}

.info-section {
  padding: 20px 16px;
}

.product-name {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  line-height: 1.4;
}

.bottom-section {
  padding: 16px;
  border-top: 1px solid #f5f5f5;
}

.navigation-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.nav-button {
  flex: 1;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
}

.back-button {
  height: 44px;
  font-size: 16px;
  font-weight: 500;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.info-card {
  animation: slideUp 0.3s ease-out;
}

:deep(.van-image) {
  width: 100% !important;
  height: 100% !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.van-image__img) {
  object-fit: contain !important;
  max-width: 100%;
  max-height: 100%;
}
</style>
